import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Auth from 'modules/Auth';
import Session from 'modules/Session';
import cx from 'classnames';
import Tooltip from 'lib/Tooltip';
import exitSvg from 'assets/icons/exit.svg';
import { SessionRecorderButton } from 'components/common/SessionRecorderButton/SessionRecorderButton';
import Local from 'modules/Local';
import IconButton from 'lib/IconButton';
import { ButtonSize, Theme } from 'lib/IconButton/IconButton.types';
import Logo from './Logo/Logo';
import MenuItem from './MenuItem/MenuItem';
import { getActiveMenuId } from './utils';
import { ItemType } from './MenuItem/MenuItem.types';
import { SidebarMenuProps } from './SidebarMenu.types';
import { SESSION_RECORDER_ENABLED } from '../SessionRecorderButton/consts';
import MenuItemBadge from './MenuItemBadge/MenuItemBadge';
import { Store } from '../types/Store.types';
import { useSidebarMenu } from './useSidebarMenu';
import { MENU_ITEMS } from './constants';

const SidebarMenu: React.FC<SidebarMenuProps> = ({ isRequiredMarketData = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pendingCreativesCount = useSelector(
    (state: Store) => state.pages.contentManagement.creatives.pendingCreativesCount,
  );
  const [activeMenuId, setActiveMenuId] = useState<number>(1);
  const menuItems = useSidebarMenu();

  const badgeCount: Record<number, number> = {
    [MENU_ITEMS.CONTENT_MANAGEMENT.id]: pendingCreativesCount ?? 0,
  };

  useEffect(() => {
    const activeId = getActiveMenuId(menuItems, location.pathname);

    if (activeId) {
      setActiveMenuId(activeId);
    }
  }, [menuItems, location]);

  const onLogoutClick = (): void => {
    Local.clear();
    Session.clear();
    Auth.logout();
  };

  const navigateHandler = (item: ItemType): void => {
    navigate(item.navigationPath);
  };

  return (
    <div
      className={cx('h-full fixed left-0 top-0 z-20 transition-all ease-in-out duration-300 bg-primary-950', {
        width: 'var(--sidebar-width)',
      })}
    >
      <Logo />
      <nav className="mt-10">
        <ul data-test-id="menu-list" className="list-none p-0 m-0">
          {menuItems.map((item) => {
            return isRequiredMarketData ? (
              <MenuItem
                key={item.id}
                item={item}
                onNavigate={() => navigateHandler(item)}
                isActive={item.id === activeMenuId}
              >
                {item.displayBadge && badgeCount[item.id] > 0 && <MenuItemBadge count={badgeCount[item.id]!} />}
              </MenuItem>
            ) : null;
          })}
        </ul>
      </nav>

      <div className="absolute w-full bottom-4 space-y-6 flex flex-col justify-center items-center">
        {SESSION_RECORDER_ENABLED && (
          <Tooltip tooltip="Record session">
            <SessionRecorderButton />
          </Tooltip>
        )}

        <IconButton
          className="fill-current rotate-180 transform"
          theme={Theme.TRANSPARENT_WHITE_TEXT}
          icon={exitSvg}
          name="logout"
          handleOnClick={onLogoutClick}
          buttonSize={ButtonSize.LARGE}
          dataTestId="logout"
          title="Logout"
        />
      </div>
    </div>
  );
};

export default SidebarMenu;
